import { createStore } from 'vuex'
import axios from 'axios'

export default createStore({
  state: {
    // Buildings
    buildings: [],
    filteredBuildings: [],
    
    // Owners
    owners: [],
    filteredOwners: [],
    
    // Maintenance Orders
    newMaintenanceOrders: [],
    filteredNewMaintenanceOrders: [],
    finishedMaintenanceOrders: [],
    filteredFinishedMaintenanceOrders: [],
    
    // Management Orders
    newManagementOrders: [],
    filteredNewManagementOrders: [],
    activeManagementOrders: [],
    filteredActiveManagementOrders: [],
    finishedManagementOrders: [],
    filteredFinishedManagementOrders: [],
    
    // Technicians
    technicians: [],
    filteredTechnicians: [],
    
    // Search state
    searchValue: '',
    currentSearchPage: '', // Track which page we're searching on
    isLoading: false
  },
  getters: {
    // Buildings
    allBuildings: state => state.buildings,
    filteredBuildings: state => state.filteredBuildings,
    
    // Owners
    allOwners: state => state.owners,
    filteredOwners: state => state.filteredOwners,
    
    // Maintenance Orders
    allNewMaintenanceOrders: state => state.newMaintenanceOrders,
    filteredNewMaintenanceOrders: state => state.filteredNewMaintenanceOrders,
    allFinishedMaintenanceOrders: state => state.finishedMaintenanceOrders,
    filteredFinishedMaintenanceOrders: state => state.filteredFinishedMaintenanceOrders,
    
    // Management Orders
    allNewManagementOrders: state => state.newManagementOrders,
    filteredNewManagementOrders: state => state.filteredNewManagementOrders,
    allActiveManagementOrders: state => state.activeManagementOrders,
    filteredActiveManagementOrders: state => state.filteredActiveManagementOrders,
    allFinishedManagementOrders: state => state.finishedManagementOrders,
    filteredFinishedManagementOrders: state => state.filteredFinishedManagementOrders,
    
    // Technicians
    allTechnicians: state => state.technicians,
    filteredTechnicians: state => state.filteredTechnicians,
    
    // Search state
    searchValue: state => state.searchValue,
    currentSearchPage: state => state.currentSearchPage,
    isLoading: state => state.isLoading
  },
  mutations: {
    // Buildings
    SET_BUILDINGS(state, buildings) {
      state.buildings = buildings
      state.filteredBuildings = buildings
    },
    SET_FILTERED_BUILDINGS(state, buildings) {
      state.filteredBuildings = buildings
    },
    
    // Owners
    SET_OWNERS(state, owners) {
      state.owners = owners
      state.filteredOwners = owners
    },
    SET_FILTERED_OWNERS(state, owners) {
      state.filteredOwners = owners
    },
    
    // Maintenance Orders
    SET_NEW_MAINTENANCE_ORDERS(state, orders) {
      state.newMaintenanceOrders = orders
      state.filteredNewMaintenanceOrders = orders
    },
    SET_FILTERED_NEW_MAINTENANCE_ORDERS(state, orders) {
      state.filteredNewMaintenanceOrders = orders
    },
    SET_FINISHED_MAINTENANCE_ORDERS(state, orders) {
      state.finishedMaintenanceOrders = orders
      state.filteredFinishedMaintenanceOrders = orders
    },
    SET_FILTERED_FINISHED_MAINTENANCE_ORDERS(state, orders) {
      state.filteredFinishedMaintenanceOrders = orders
    },
    
    // Management Orders
    SET_NEW_MANAGEMENT_ORDERS(state, orders) {
      state.newManagementOrders = orders
      state.filteredNewManagementOrders = orders
    },
    SET_FILTERED_NEW_MANAGEMENT_ORDERS(state, orders) {
      state.filteredNewManagementOrders = orders
    },
    SET_ACTIVE_MANAGEMENT_ORDERS(state, orders) {
      state.activeManagementOrders = orders
      state.filteredActiveManagementOrders = orders
    },
    SET_FILTERED_ACTIVE_MANAGEMENT_ORDERS(state, orders) {
      state.filteredActiveManagementOrders = orders
    },
    SET_FINISHED_MANAGEMENT_ORDERS(state, orders) {
      state.finishedManagementOrders = orders
      state.filteredFinishedManagementOrders = orders
    },
    SET_FILTERED_FINISHED_MANAGEMENT_ORDERS(state, orders) {
      state.filteredFinishedManagementOrders = orders
    },
    
    // Technicians
    SET_TECHNICIANS(state, technicians) {
      state.technicians = technicians
      state.filteredTechnicians = technicians
    },
    SET_FILTERED_TECHNICIANS(state, technicians) {
      state.filteredTechnicians = technicians
    },
    
    // Search state
    SET_SEARCH_VALUE(state, value) {
      state.searchValue = value
    },
    SET_CURRENT_SEARCH_PAGE(state, page) {
      state.currentSearchPage = page
    },
    SET_LOADING(state, loading) {
      state.isLoading = loading
    },
    RESET_SEARCH(state) {
      state.searchValue = ''
      state.currentSearchPage = ''
      state.filteredBuildings = state.buildings
      state.filteredOwners = state.owners
      state.filteredNewMaintenanceOrders = state.newMaintenanceOrders
      state.filteredFinishedMaintenanceOrders = state.finishedMaintenanceOrders
      state.filteredTechnicians = state.technicians
      state.filteredNewManagementOrders = state.newManagementOrders
      state.filteredActiveManagementOrders = state.activeManagementOrders
      state.filteredFinishedManagementOrders = state.finishedManagementOrders
    }
  },
  actions: {
    // Buildings
    async fetchBuildings({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/buildings', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_BUILDINGS', response.data.data)
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching buildings:', error)
        commit('SET_LOADING', false)
      }
    },
    
    // Owners
    async fetchOwners({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/owners', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_OWNERS', response.data.data)
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching owners:', error)
        commit('SET_LOADING', false)
      }
    },
    
    // Maintenance Orders
    async fetchNewMaintenanceOrders({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/new-maintenance-orders', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_NEW_MAINTENANCE_ORDERS', response.data.data.orders)
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching new maintenance orders:', error)
        commit('SET_LOADING', false)
      }
    },
    
    async fetchFinishedMaintenanceOrders({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/finished-maintenance-orders', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_FINISHED_MAINTENANCE_ORDERS', response.data.data?.orders || [])
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching finished maintenance orders:', error)
        commit('SET_LOADING', false)
      }
    },
    
    // Technicians
    async fetchTechnicians({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/workers', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_TECHNICIANS', response.data.data)
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching technicians:', error)
        commit('SET_LOADING', false)
      }
    },
    
    // Management Orders
    async fetchNewManagementOrders({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/new-management-orders', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_NEW_MANAGEMENT_ORDERS', response.data.data.orders)
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching new management orders:', error)
        commit('SET_LOADING', false)
      }
    },
    
    async fetchActiveManagementOrders({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/active-management-orders', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_ACTIVE_MANAGEMENT_ORDERS', response.data.data.orders)
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching active management orders:', error)
        commit('SET_LOADING', false)
      }
    },
    
    async fetchFinishedManagementOrders({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await axios.get('/manager/finished-management-orders', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        commit('SET_FINISHED_MANAGEMENT_ORDERS', response.data.data.orders)
        commit('SET_LOADING', false)
      } catch (error) {
        console.error('Error fetching finished management orders:', error)
        commit('SET_LOADING', false)
      }
    },
    
    // Search functionality
    searchItems({ commit, state }, { value, page }) {
      commit('SET_SEARCH_VALUE', value)
      commit('SET_CURRENT_SEARCH_PAGE', page)
      
      if (!value.trim()) {
        // Reset all filters for current page
        if (page === 'buildingsView') {
          commit('SET_FILTERED_BUILDINGS', state.buildings)
        } else if (page === 'ownersView') {
          commit('SET_FILTERED_OWNERS', state.owners)
        } else if (page === 'MaintenanceView') {
          commit('SET_FILTERED_NEW_MAINTENANCE_ORDERS', state.newMaintenanceOrders)
          commit('SET_FILTERED_FINISHED_MAINTENANCE_ORDERS', state.finishedMaintenanceOrders)
        } else if (page === 'TechniciansView') {
          commit('SET_FILTERED_TECHNICIANS', state.technicians)
        } else if (page === 'managementOrders') {
          commit('SET_FILTERED_NEW_MANAGEMENT_ORDERS', state.newManagementOrders)
          commit('SET_FILTERED_ACTIVE_MANAGEMENT_ORDERS', state.activeManagementOrders)
          commit('SET_FILTERED_FINISHED_MANAGEMENT_ORDERS', state.finishedManagementOrders)
        }
        return
      }
      
      const searchTerm = value.toLowerCase().trim()
      
      // Search buildings
      if (page === 'buildingsView') {
        const filtered = state.buildings.filter(building => {
          return (
            building.name?.toLowerCase().includes(searchTerm) ||
            building.ref_code?.toLowerCase().includes(searchTerm) ||
            building.owner?.name?.toLowerCase().includes(searchTerm) ||
            building.owner?.phone?.includes(searchTerm)
          )
        })
        commit('SET_FILTERED_BUILDINGS', filtered)
      }
      
      // Search owners
      else if (page === 'ownersView') {
        const filtered = state.owners.filter(owner => {
          return (
            owner.name?.toLowerCase().includes(searchTerm) ||
            owner.phone?.includes(searchTerm) ||
            owner.ref_types?.toLowerCase().includes(searchTerm) ||
            owner.buildings?.toLowerCase().includes(searchTerm) ||
            owner.ref_codes?.toLowerCase().includes(searchTerm)
          )
        })
        commit('SET_FILTERED_OWNERS', filtered)
      }
      
      // Search maintenance orders
      else if (page === 'MaintenanceView') {
        // Filter new orders
        const filteredNew = state.newMaintenanceOrders.filter(order => {
          return (
            order.id?.toString().includes(searchTerm) ||
            order.name?.toLowerCase().includes(searchTerm) ||
            order.user?.name?.toLowerCase().includes(searchTerm) ||
            order.maintenance_type?.name?.toLowerCase().includes(searchTerm) ||
            order.worker?.name?.toLowerCase().includes(searchTerm)
          )
        })
        commit('SET_FILTERED_NEW_MAINTENANCE_ORDERS', filteredNew)
        
        // Filter finished orders
        const filteredFinished = state.finishedMaintenanceOrders.filter(order => {
          return (
            order.id?.toString().includes(searchTerm) ||
            order.name?.toLowerCase().includes(searchTerm) ||
            order.user?.name?.toLowerCase().includes(searchTerm) ||
            order.maintenance_type?.name?.toLowerCase().includes(searchTerm) ||
            order.worker?.name?.toLowerCase().includes(searchTerm)
          )
        })
        commit('SET_FILTERED_FINISHED_MAINTENANCE_ORDERS', filteredFinished)
      }
      
      // Search technicians
      else if (page === 'TechniciansView') {
        const filtered = state.technicians.filter(technician => {
          return (
            technician.name?.toLowerCase().includes(searchTerm) ||
            technician.phone?.includes(searchTerm) ||
            technician.maintenance_type?.name?.toLowerCase().includes(searchTerm) ||
            technician.ref_codes?.toLowerCase().includes(searchTerm) ||
            technician.rate?.toString().includes(searchTerm)
          )
        })
        commit('SET_FILTERED_TECHNICIANS', filtered)
      }
      
      // Search management orders
      else if (page === 'managementOrders') {
        // Filter new orders
        const filteredNew = state.newManagementOrders.filter(order => {
          return (
            order.id?.toString().includes(searchTerm) ||
            order.ref_code?.toLowerCase().includes(searchTerm) ||
            order.type?.name?.toLowerCase().includes(searchTerm) ||
            order.owner?.name?.toLowerCase().includes(searchTerm) ||
            order.owner?.phone?.includes(searchTerm)
          )
        })
        commit('SET_FILTERED_NEW_MANAGEMENT_ORDERS', filteredNew)
        
        // Filter active orders
        const filteredActive = state.activeManagementOrders.filter(order => {
          return (
            order.id?.toString().includes(searchTerm) ||
            order.ref_code?.toLowerCase().includes(searchTerm) ||
            order.type?.name?.toLowerCase().includes(searchTerm) ||
            order.owner?.name?.toLowerCase().includes(searchTerm) ||
            order.owner?.phone?.includes(searchTerm)
          )
        })
        commit('SET_FILTERED_ACTIVE_MANAGEMENT_ORDERS', filteredActive)
        
        // Filter finished orders
        const filteredFinished = state.finishedManagementOrders.filter(order => {
          return (
            order.id?.toString().includes(searchTerm) ||
            order.ref_code?.toLowerCase().includes(searchTerm) ||
            order.type?.name?.toLowerCase().includes(searchTerm) ||
            order.owner?.name?.toLowerCase().includes(searchTerm) ||
            order.owner?.phone?.includes(searchTerm)
          )
        })
        commit('SET_FILTERED_FINISHED_MANAGEMENT_ORDERS', filteredFinished)
      }
    },
    
    resetSearch({ commit }) {
      commit('RESET_SEARCH')
    }
  },
  modules: {
  }
})
